import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'
import { IconMessage, IconMessagePlus } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'

import CommentComposer from './CommentComposer'
import { getSelectionRangeWithin, rangeToOffsets } from './selectionOffsets'
import './SelectionCommentPopover.scss'

// =============================================================================
// SelectionCommentPopover — watches for a text selection inside `containerRef`,
// floats an "Add comment" affordance over it, and opens a CommentComposer. On
// submit it hands the caller the selection's offsets + quoted snippet so the
// caller can build the request-type-specific payload.
//
// The snippet is sliced from `baseText` (not the DOM) so it matches the exact
// string the offsets index into — the transcript BE re-checks this equality and
// rejects a mismatch, so reading it off the DOM (with its <mark> splits and
// whitespace) would break commenting.
// =============================================================================

// Breathing room the popover keeps from the viewport edge.
const MARGIN = 8

// Position is cosmetic — guard against environments where a Range can't measure
// itself (jsdom) so a missing rect never blocks commenting.
const measureRangeRect = (range) => {
  try {
    return range.getBoundingClientRect()
  } catch (rectError) {
    return { top: 0, left: 0, width: 0, bottom: 0 }
  }
}

// True when the gesture ran against document order (R→L / bottom→top), i.e. the
// focus point precedes the anchor point. Used to open the pill on the matching
// side (forward → below the end, backward → above the start).
const isSelectionBackward = (selection) => {
  try {
    const { anchorNode, anchorOffset, focusNode, focusOffset } = selection
    if (isNil(anchorNode) || isNil(focusNode)) {
      return false
    }
    // comparePoint against a caret collapsed at the anchor: -1 means the focus
    // sits before the anchor in document order → the drag ran backward.
    const probe = document.createRange()
    probe.setStart(anchorNode, anchorOffset)
    probe.setEnd(anchorNode, anchorOffset)
    return probe.comparePoint(focusNode, focusOffset) < 0
  } catch (directionError) {
    return false
  }
}

// The selection's visual end-of-gesture edge, read from the range's OWN client
// rects (one box per line). Forward → right edge of the last box; backward →
// left edge of the first. This is reliable viewport geometry of the actual
// selection — unlike reconstructing a caret from focusNode/focusOffset, which
// mis-resolves to a wrong boundary when the focus lands on an element/segment
// span (e.g. the prompt's mark/`data-segment-index` wrappers) and threw the pill
// to the far side of the block. Falls back to the bounding rect (jsdom: no layout).
const measureGestureEdge = (range, backward) => {
  const rects = typeof range.getClientRects === 'function' ? range.getClientRects() : null
  const rect = !isEmpty(rects) ? rects[backward ? 0 : rects.length - 1] : measureRangeRect(range)
  return { top: rect.top, bottom: rect.bottom, left: backward ? rect.left : rect.right }
}

// Resolve a selection range to the popover anchor + pending offsets/snippet, or
// null when the offsets are empty/invalid.
const readSelection = (container, range, resolveOffsets, baseText) => {
  const offsets = isNil(resolveOffsets) ? rangeToOffsets(container, range) : resolveOffsets(range)
  if (isNil(offsets) || offsets.end <= offsets.start) {
    return null
  }
  const backward = isSelectionBackward(window.getSelection())
  const edge = measureGestureEdge(range, backward)
  const rect = measureRangeRect(range)
  return {
    anchor: {
      // `left` = the gesture-end x (selection's right edge for a forward drag,
      // left edge for a backward one), where the pill sits; it opens on the
      // gesture's side — below the end for forward, above the start for backward.
      // The composer instead centers on `centerLeft` (the selection's horizontal
      // midpoint) and always lifts above the whole passage, so neither ever
      // covers the text it's anchored to.
      left: edge.left,
      centerLeft: rect.left + rect.width / 2,
      pillTop: backward ? edge.top : edge.bottom,
      pillBelow: !backward,
      selectionTop: rect.top,
    },
    pending: {
      start: offsets.start,
      end: offsets.end,
      snippet: baseText.slice(offsets.start, offsets.end),
    },
  }
}

// The existing comment that overlaps `pending` the most, or null. Overlap is
// computed against re-anchored ranges (same as the painted marks), so a stale
// stored offset can't make us miss/false-positive an overlap.
const pickOverlap = (existingRanges, pending) => {
  if (isNil(pending) || isEmpty(existingRanges)) {
    return null
  }
  let best = null
  let bestAmount = 0
  existingRanges.forEach((range) => {
    const amount = Math.min(range.end, pending.end) - Math.max(range.start, pending.start)
    if (amount > 0 && amount > bestAmount) {
      best = range.id
      bestAmount = amount
    }
  })
  return best
}

/**
 * @param {object} props.containerRef ref to the element whose text is commentable
 * @param {string} props.baseText canonical string the offsets index into
 * @param {boolean} [props.enabled] gate selection-commenting (e.g. only assistant turns)
 * @param {boolean} [props.busy] disable submit while a create is in flight
 * @param {Function} props.onSubmit ({start,end,snippet,message}) => Promise<created|null>
 * @param {Function} [props.resolveOffsets] (range) => {start,end} — override the
 *   default text-node walk when the DOM diverges from baseText (segmented prompt)
 * @param {object[]} [props.existingRanges] re-anchored open-comment ranges (`[{id,start,end}]`)
 * @param {Function} [props.onOpenExisting] (id) => void — focus an overlapped comment instead of creating
 * @param {Function} [props.onPendingChange] ({start,end,snippet}|null) => void — the live
 *   selection, so the host can paint a persistent draft highlight over it
 * @param {Function} [props.onActiveChange] (active: boolean) => void — true from the
 *   moment a selection starts through the pill/composer, false once the interaction
 *   ends (submit/cancel/dismiss), so the host can freeze transcript auto-scroll
 * @param {Function} [props.onComposingChange] (composing: boolean) => void — narrower
 *   than onActiveChange: true only while the composer itself is open. Selecting text
 *   must not touch audio playback; writing a comment pauses and locks it (AVR-875)
 * @param {boolean} [props.requiresArming] two-step mode: the host owns the
 *   affordance and text is not selectable until it arms this layer. A selection made
 *   while armed goes straight to the composer — the pill step already happened, as
 *   the host's own button. Default (false) is the one-step mode the prompt tab uses:
 *   any selection raises the pill, which then opens the composer
 * @param {boolean} [props.armed] armed mode only: the host's affordance was pressed,
 *   so this layer should now watch for a selection
 * @param {Function} [props.onDisarm] () => void — the interaction ended (submit,
 *   cancel, Esc, click-away); clear the host's armed flag
 */
const SelectionCommentPopover = ({
  containerRef,
  baseText,
  enabled = true,
  busy = false,
  onSubmit,
  resolveOffsets,
  existingRanges = [],
  onOpenExisting,
  onPendingChange,
  onActiveChange,
  onComposingChange,
  requiresArming = false,
  armed = false,
  onDisarm,
}) => {
  const popoverRef = useRef(null)
  // Read inside the native mouseup handler. Held in a ref rather than closed
  // over so `existingRanges` (rebuilt every render by the host) can't churn the
  // document listeners.
  const overlapRef = useRef(null)
  overlapRef.current = { existingRanges, onOpenExisting }
  const [anchor, setAnchor] = useState(null)
  const [pending, setPending] = useState(null)
  const [composing, setComposing] = useState(false)
  // True while a selection gesture is underway (mousedown → mouseup inside the
  // container), before the pill exists. Freezes auto-scroll from selection start.
  const [selecting, setSelecting] = useState(false)

  const clear = useCallback(() => {
    setAnchor(null)
    setPending(null)
    setComposing(false)
    setSelecting(false)
    if (!isNil(onDisarm)) {
      onDisarm()
    }
  }, [onDisarm])

  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    // Armed mode: nothing is watched until the host's affordance is pressed, so
    // an idle transcript can't raise a comment UI by accident. Keep listening
    // while composing, which is how a second selection narrows the comment.
    if (requiresArming && !armed && !composing) {
      return undefined
    }

    // A press inside the commentable text begins a potential selection — freeze
    // auto-scroll now so the text can't slide before the gesture finishes.
    const handleMouseDown = (event) => {
      const container = containerRef?.current
      if (!isNil(container) && container.contains(event.target)) {
        setSelecting(true)
      }
    }

    const handleMouseUp = (event) => {
      // Read the container live — it may attach after this effect first runs.
      // The popover's own clicks must not be read as a new selection attempt.
      const container = containerRef?.current
      if (isNil(container) || popoverRef.current?.contains(event.target)) {
        setSelecting(false)
        return
      }
      const range = getSelectionRangeWithin(container)
      if (isNil(range)) {
        // A collapsed mouseup inside the commentable text is not a reason to
        // abandon the interaction. In armed mode it is the first click of a
        // double-click — which is how you select a word — and clearing here
        // would put `user-select: none` back before the second click landed.
        // Staying armed is safe: Esc and a press outside the text still back
        // out (see the effect below).
        const insideArmedText = requiresArming && armed && container.contains(event.target)
        if (!composing && !insideArmedText) {
          clear()
        }
        setSelecting(false)
        return
      }
      const next = readSelection(container, range, resolveOffsets, baseText)
      if (isNil(next)) {
        clear()
        return
      }
      // Set the pill BEFORE dropping `selecting`. React 16 doesn't batch state
      // updates inside this native document listener, so each flushes synchronously:
      // clearing `selecting` first would briefly render `selecting:false, anchor:null`
      // — an inactive frame that releases the auto-scroll freeze and snaps the viewer
      // to the live turn at the instant of release. Anchor-first keeps the active
      // signal continuous (no false frame) on both React 16 and any batched React.
      setAnchor(next.anchor)
      setPending(next.pending)
      // Armed mode goes straight to the composer: the pill step already
      // happened as the host's button, so stopping for a second one would be
      // asking twice. The exception is a selection landing on a comment that
      // already exists, where offering to open that one beats creating a
      // duplicate highlight — that still needs the pill.
      const { existingRanges: ranges, onOpenExisting: openExisting } = overlapRef.current
      const overlaps = !isNil(openExisting) && !isNil(pickOverlap(ranges, next.pending))
      if (requiresArming && !overlaps) {
        setComposing(true)
      }
      // Otherwise `composing` is deliberately left alone: a selection made
      // while the composer is open narrows the comment to that span instead of
      // throwing the draft away — the composer stays mounted, so the typed text
      // survives.
      setSelecting(false)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [enabled, requiresArming, armed, containerRef, baseText, resolveOffsets, composing, clear])

  // Armed, waiting for the selection that is step two. Nothing is on screen yet
  // (the host is drawing the prompt), so the dismissal effect below — which
  // hangs off `anchor` — can't cover it: Esc and a click outside the text are
  // the way back out of a flow you started by mistake.
  useEffect(() => {
    if (!requiresArming || !armed || !isNil(anchor)) {
      return undefined
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clear()
      }
    }
    const handlePointerDown = (event) => {
      const container = containerRef?.current
      if (!isNil(container) && !container.contains(event.target)) {
        clear()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [requiresArming, armed, anchor, containerRef, clear])

  // Losing the gate mid-interaction — the prompt tab peeking a read-only older
  // version — has to take the UI with it. The listeners detach on their own,
  // but an already-open composer would float over content it is no longer
  // allowed to comment on, and its Submit would still post.
  useEffect(() => {
    if (!enabled) {
      clear()
    }
  }, [enabled, clear])

  // Dismiss on click-away / Esc once the affordance is showing. We deliberately
  // do NOT dismiss on scroll: the transcript auto-scrolls during playback (and
  // on seek), which would tear the popover down the instant it appeared. The
  // trade-off is the fixed-position popover drifts on manual scroll instead of
  // closing — acceptable for the pill, which users act on immediately. The
  // composer deliberately survives click-away (only Submit/Cancel/Esc close
  // it), so it rides out a scroll too.
  useEffect(() => {
    if (isNil(anchor)) {
      return undefined
    }
    const handlePointerDown = (event) => {
      // Only the pill dismisses on click-away. Once the composer is open it
      // stays open until Submit, Cancel or Esc — clicking the transcript to
      // re-read the line you are commenting on used to discard the draft, and
      // selecting a narrower span is a click outside the popover by definition.
      if (!composing && !popoverRef.current?.contains(event.target)) {
        clear()
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clear()
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [anchor, composing, clear])

  // Surface the live selection so the host can keep a draft highlight painted
  // over it — the native selection collapses the instant the composer textarea
  // takes focus, so it can't be relied on to keep the chunk outlined.
  useEffect(() => {
    if (!isNil(onPendingChange)) {
      onPendingChange(pending)
    }
  }, [pending, onPendingChange])

  // Report whether a comment interaction is in progress so the host can freeze
  // transcript auto-scroll. `anchor` covers both the pill and the composer phase
  // (the native selection collapses on textarea focus, so it can't be the source).
  useEffect(() => {
    if (!isNil(onActiveChange)) {
      onActiveChange(selecting || !isNil(anchor))
    }
  }, [selecting, anchor, onActiveChange])

  // Narrower than the report above: only an open composer pauses and locks the
  // player. A selection on its own must leave playback exactly as it found it.
  useEffect(() => {
    if (!isNil(onComposingChange)) {
      onComposingChange(composing)
    }
  }, [composing, onComposingChange])

  // Release every host flag if the layer is torn down mid-interaction (a search
  // or the editor replacing the commentable content). Read through a ref with
  // an empty dep array so this runs on a real unmount and nothing else: with
  // the callbacks as deps, a host passing an inline arrow would re-run the
  // cleanup on every render and silently cancel its own interaction.
  const releaseRef = useRef(null)
  releaseRef.current = { onActiveChange, onComposingChange, onDisarm }
  useEffect(() => {
    return () => {
      const release = releaseRef.current
      if (!isNil(release.onActiveChange)) {
        release.onActiveChange(false)
      }
      if (!isNil(release.onComposingChange)) {
        release.onComposingChange(false)
      }
      if (!isNil(release.onDisarm)) {
        release.onDisarm()
      }
    }
  }, [])

  // Keep the popover inside the viewport. It's fixed-position; the centered
  // composer can spill off either edge near the drawer's sides, so we measure
  // the placed rect and pull it back in. React applies `--popover-left` before
  // this layout effect runs, so the correction happens pre-paint (no flicker).
  useLayoutEffect(() => {
    const node = popoverRef.current
    if (isNil(node)) {
      return
    }
    const baseLeft = composing ? anchor.centerLeft : anchor.left
    const rect = node.getBoundingClientRect()
    let left = baseLeft
    if (rect.right > window.innerWidth - MARGIN) {
      left -= rect.right - (window.innerWidth - MARGIN)
    }
    const shiftedLeft = rect.left + (left - baseLeft)
    if (shiftedLeft < MARGIN) {
      left += MARGIN - shiftedLeft
    }
    if (left !== baseLeft) {
      node.style.setProperty('--popover-left', `${left}px`)
    }
  }, [anchor, composing])

  if (isNil(anchor) || isNil(pending)) {
    return null
  }

  const handleSubmit = async (message) => {
    const result = await onSubmit({ ...pending, message })
    // Keep the composer open on failure so the user doesn't lose their text.
    if (result) {
      clear()
    }
  }

  // A selection overlapping an existing comment offers to open it rather than
  // create a confusing duplicate highlight.
  const overlapId = isNil(onOpenExisting) ? null : pickOverlap(existingRanges, pending)

  const renderAction = () => {
    if (composing) {
      return <CommentComposer onSubmit={handleSubmit} onCancel={clear} busy={busy} />
    }
    // The icon button(s) live inside an elevated white pill (the toolbar); the
    // composer renders outside it, unframed.
    return (
      <div className="comment-selection-popover__toolbar">
        {isNil(overlapId) ? (
          <Button
            basic
            circular
            small
            icon={<IconMessagePlus />}
            tooltip="Add comment"
            tooltipProps={{ placement: anchor.pillBelow ? 'bottom' : 'top' }}
            aria-label="Add comment"
            onClick={() => setComposing(true)}
            data-testid="selection-comment-add"
          />
        ) : (
          <Button
            basic
            circular
            small
            icon={<IconMessage />}
            tooltip="Open existing comment"
            tooltipProps={{ placement: anchor.pillBelow ? 'bottom' : 'top' }}
            aria-label="Open existing comment"
            onClick={() => {
              onOpenExisting(overlapId)
              clear()
            }}
            data-testid="selection-comment-open-existing"
          />
        )}
      </div>
    )
  }

  // Composer centers on the selection and lifts above the whole passage; the
  // pill opens on the gesture's side (below a forward drag, above a backward
  // one) at the gesture-end x — neither ever covers the selected text. The scss
  // modifiers flip the transform: --composer centers + lifts, --below pushes down.
  const popoverTop = composing ? anchor.selectionTop : anchor.pillTop
  const popoverLeft = composing ? anchor.centerLeft : anchor.left

  return (
    <div
      ref={popoverRef}
      className={classNames('comment-selection-popover', {
        'comment-selection-popover--composer': composing,
        'comment-selection-popover--below': !composing && anchor.pillBelow,
      })}
      style={{ '--popover-top': `${popoverTop}px`, '--popover-left': `${popoverLeft}px` }}
      data-testid="selection-comment-popover"
      // The popover renders inside the transcript turn, whose onClick seeks the
      // player to that turn. Keep pill/textarea/button clicks from bubbling there
      // so interacting with the comment UI doesn't rewind and restart the audio.
      onClick={(event) => event.stopPropagation()}
    >
      {renderAction()}
    </div>
  )
}

export default SelectionCommentPopover
